import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { hasAllPermissions, hasPermission, useCheckPermissions, useTableFilter } from '@/shared';
import { useFetchCentrosCostos, useUpdateCentrosCostos } from '@/shared/actions/erp/administration';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import { CENTROCOSTO } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';
import { PermissionsEnum } from '@/auth/interfaces';

const returnUrlCentro = '/parametrizacion/centro-costo';

export interface CentroCostosPageInterface {}

const CentrosCostosPage: React.FC<CentroCostosPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_centrocosto)

  const navigate = useNavigate();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex } = pagination;

  ///* fetch data
  const {
    data: centrosCodigoPagingRes,
    isLoading,
    isRefetching,
  } = useFetchCentrosCostos({
    page: pageIndex + 1,
    nombre: searchTerm,
  });

  ///* mutations
  const updateCentroCostosMutation = useUpdateCentrosCostos({navigate, returnUrl:returnUrlCentro});
  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  ///* handlers
  const onEdit = (original: CENTROCOSTO) => {
    navigate(`${returnUrlCentro}/editar/${original.id_centro_costo}`);
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<CENTROCOSTO>[]>(
    () => [
      {
        accessorKey: 'numero_referencia',
        header: 'Numero referencia',
        size: 300,
      },
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 300,
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? 'Activo' : 'Inactivo'}
            setEstadoSwitch={centro_costo => {
              if (!hasPermission(PermissionsEnum.api_change_centrocosto)) return;
              setConfirmDialog({
                isOpen: true,
                title: 'Cambiar Estado',
                subtitle:
                  '¿Está seguro que desea cambiar el estado de este registro ?',
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  updateCentroCostosMutation.mutate({
                    id: row.original.id_centro_costo!,
                    data: {
                      ...row.original,
                      estado: centro_costo as boolean,
                    },
                  });
                },
              });
            }}
          />
        ),
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene title="Centro de costos" urlRedirect={`${returnUrlCentro}/crear`} enableRedirect={hasPermission(PermissionsEnum.api_add_centrocosto)}>
      <CustomSearch onChange={onChangeFilter} value={globalFilter} text="centro de costos" />

      <GeneralTable<CENTROCOSTO>
        columns={columns}
        data={centrosCodigoPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={centrosCodigoPagingRes?.data?.count}
        // // actions
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_centrocosto,
        ])}
        // crud
        onEdit={onEdit}
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_centrocosto,
        ])}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default CentrosCostosPage;
